import { UiSuspensefulOutlet } from '@experiences/ui-common';
import type { UiRouteObject } from '@experiences/util';
import React from 'react';
import {
    type PropsWithChildren,
    useMemo,
} from 'react';
import { useIntl } from 'react-intl';

import { routePaths } from '../../common/constants/routePaths';
import { useOrganizationName } from '../../common/hooks/useOrganizationName';
import BreadcrumbProvider from '../../common/providers/BreadcrumbProvider';
import { useTenantIdContext } from '../../common/providers/TenantIdProvider';
import RegisterRelayGroup from './RegisterRelayGroup';
import RelayClientSetupPageComponent from './RelayClientSetupPageComponent';
import RelayGroupDetailsPageComponent from './RelayGroupDetailsPageComponent';
import RelaySettingsPageComponent from './RelaySettingsPageComponent';

const RelaySettingsProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const { formatMessage: translate } = useIntl();
    const organizationName = useOrganizationName();
    const { tenantName } = useTenantIdContext();

    const breadCrumbLinks = useMemo(() => [
        {
            index: 0,
            link: routePaths.admin,
            name: organizationName,
        },
        {
            index: 1,
            link: routePaths.adminTenantHome,
            name: tenantName,
        },
        {
            index: 2,
            link: routePaths.adminTenantRelay,
            name: translate({ id: 'CLIENT_RELAY' }),
        },
        {
            index: 3,
            link: routePaths.adminTenantRelayAdd,
            name: translate({ id: 'CLIENT_REGISTER_RELAY' }),
        },
        {
            index: 3,
            link: routePaths.adminTenantRelayEdit,
            name: translate({ id: 'CLIENT_EDIT_RELAY' }),
        },
    ], [ organizationName, tenantName, translate ]);

    return <BreadcrumbProvider breadcrumbs={breadCrumbLinks}>
        {children}
    </BreadcrumbProvider>;
};

export const RelaySettingsRoutes: UiRouteObject = {
    path: routePaths.adminTenantRelay,
    element: <RelaySettingsProvider>
        <UiSuspensefulOutlet />
    </RelaySettingsProvider>,
    children: [
        {
            index: true,
            element: <RelaySettingsPageComponent />,
        },
        {
            path: routePaths.adminTenantRelayAdd,
            element: <RegisterRelayGroup type='add' />,
        },
        {
            path: routePaths.adminTenantRelayDetails,
            element: <RelayGroupDetailsPageComponent />,
        },
        {
            path: routePaths.adminTenantRelayEdit,
            element: <RegisterRelayGroup type='edit' />,
        },
        {
            path: routePaths.adminTenantRelaySetupInstructions,
            element: <RelayClientSetupPageComponent />,
        },
    ],
};
